import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { Play, Square, RotateCcw, Trash2, Monitor, Activity, Settings, FileText, Database } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const metricsData = Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, cpu: Math.floor(Math.random() * 60) + 10, ram: Math.floor(Math.random() * 40) + 30 }));
const msgData = Array.from({ length: 7 }, (_, i) => ({ day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i], messages: Math.floor(Math.random() * 300) + 50 }));

export default function InstanceDetails() {
  const { id } = useParams();
  const [instance, setInstance] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => { api.getInstance(id).then(setInstance); }, [id]);

  if (!instance) return <div className="text-zinc-500 font-mono">Loading...</div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'metrics', label: 'Metrics', icon: Activity },
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'backups', label: 'Backups', icon: Database },
  ];

  return (
    <div data-testid="instance-details-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">{instance.name}</h1>
          <p className="text-sm text-zinc-500 font-mono">{instance.id} &middot; {instance.region}</p>
        </div>
        <div className="flex gap-2">
          {instance.status === 'stopped' ? (
            <button data-testid="instance-start" className="flex items-center gap-2 px-4 py-2 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-sm hover:bg-emerald-500/20 transition-all"><Play size={14} /> Start</button>
          ) : (
            <button data-testid="instance-stop" className="flex items-center gap-2 px-4 py-2 rounded-sm bg-zinc-800 text-zinc-400 border border-zinc-700 font-mono text-sm hover:text-red-400 hover:border-red-500/30 transition-all"><Square size={14} /> Stop</button>
          )}
          <button data-testid="instance-restart" className="flex items-center gap-2 px-4 py-2 rounded-sm border border-zinc-800 text-zinc-400 font-mono text-sm hover:border-zinc-700 transition-all"><RotateCcw size={14} /> Restart</button>
          <Link to={`/dashboard/instances/${id}/rdp`} data-testid="instance-rdp-btn" className="flex items-center gap-2 px-4 py-2 rounded-sm border border-cyan-500/20 text-cyan-400 font-mono text-sm hover:bg-cyan-500/10 transition-all"><Monitor size={14} /> RDP</Link>
          <button data-testid="instance-delete" className="flex items-center gap-2 px-4 py-2 rounded-sm border border-zinc-800 text-zinc-500 font-mono text-sm hover:text-red-400 hover:border-red-500/30 transition-all"><Trash2 size={14} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-px">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-mono transition-all border-b-2 ${tab === t.id ? 'text-emerald-400 border-emerald-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Status', value: instance.status, color: instance.status === 'running' ? 'text-emerald-400' : 'text-zinc-500' },
            { label: 'IP Address', value: instance.ip, color: 'text-cyan-400' },
            { label: 'Uptime', value: instance.uptime, color: 'text-white' },
            { label: 'OpenClaw', value: `v${instance.clawVersion}`, color: 'text-white' },
            { label: 'CPU', value: `${instance.cpu}%`, color: 'text-white' },
            { label: 'RAM', value: `${instance.ram}%`, color: 'text-white' },
            { label: 'Disk', value: `${instance.disk}%`, color: 'text-white' },
            { label: 'Messages', value: instance.messages.toLocaleString(), color: 'text-emerald-400' },
          ].map((s, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
              <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{s.label}</span>
              <p className={`text-lg font-mono font-bold mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'metrics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-zinc-400 mb-4">CPU & RAM (24h)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={metricsData}>
                <XAxis dataKey="hour" stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10 }} />
                <YAxis stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #27272A', borderRadius: 2, fontSize: 12 }} />
                <Area type="monotone" dataKey="cpu" stroke="#10B981" fill="rgba(16,185,129,0.1)" strokeWidth={2} />
                <Area type="monotone" dataKey="ram" stroke="#06B6D4" fill="rgba(6,182,212,0.1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-zinc-400 mb-4">Messages (7d)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={msgData}>
                <XAxis dataKey="day" stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10 }} />
                <YAxis stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #27272A', borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="messages" fill="#10B981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === 'config' && (
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 space-y-4">
          {[['Model', instance.model], ['Channel', instance.channel], ['Tier', instance.tier], ['Region', instance.region], ['Created', instance.createdAt]].map(([k, v]) => (
            <div key={k} className="flex justify-between items-center py-2 border-b border-zinc-800/50 last:border-0">
              <span className="text-sm font-mono text-zinc-500">{k}</span>
              <span className="text-sm font-mono text-white">{v}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'logs' && (
        <div className="terminal-window">
          <div className="terminal-header">
            <div className="terminal-dot bg-red-500/80" /><div className="terminal-dot bg-yellow-500/80" /><div className="terminal-dot bg-green-500/80" />
            <span className="text-xs text-zinc-500 ml-2 font-mono">logs — {instance.name}</span>
          </div>
          <div className="terminal-body text-xs leading-6 max-h-96 overflow-y-auto">
            {['[INFO] OpenClaw v0.8.2 started', '[INFO] Loading skills: gmail, github, calendar', '[INFO] Telegram bot connected', `[INFO] Model: ${instance.model}`, '[INFO] Memory loaded: 2,847 entries', '[INFO] Heartbeat check: OK', '[DEBUG] Processing message from user', '[INFO] Skill invoked: gmail.read_inbox', '[INFO] Response generated (347 tokens)', '[INFO] Message sent via Telegram', '[DEBUG] Memory updated: +1 entry', '[INFO] Heartbeat check: OK'].map((log, i) => (
              <div key={i}><span className="text-zinc-600">{`2026-02-${10 + i} 1${i}:${30 + i}:00`}</span> <span className={log.includes('[INFO]') ? 'text-emerald-400' : 'text-zinc-500'}>{log}</span></div>
            ))}
          </div>
        </div>
      )}

      {tab === 'backups' && (
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-zinc-800"><th className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase">Date</th><th className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase">Type</th><th className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase">Size</th><th className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase">Actions</th></tr></thead>
            <tbody>
              {[{ date: '2026-02-20', type: 'Automated', size: '2.4 GB' }, { date: '2026-02-19', type: 'Automated', size: '2.3 GB' }, { date: '2026-02-18', type: 'Manual', size: '2.3 GB' }].map((b, i) => (
                <tr key={i} className="border-b border-zinc-800/50"><td className="py-3 px-4 text-sm font-mono text-white">{b.date}</td><td className="py-3 px-4 text-sm font-mono text-zinc-400">{b.type}</td><td className="py-3 px-4 text-sm font-mono text-zinc-400">{b.size}</td><td className="py-3 px-4"><button className="text-xs font-mono text-emerald-400 hover:underline">Restore</button></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
